import {
    useAvailableTimes,
    useFilteredAvailableTimes,
    useFilteredExperiences,
} from "api/api-hooks/use-available-times";
import { useExternalBookingSettings } from "api/api-hooks/use-external-booking-settings";
import SomethingWrong from "components/error-components/something-wrong";
import { useBookingStateContext } from "hooks/use-booking-state";
import { useStepTracking } from "logger/use-step-tracking";
import { usePageTracking } from "logger/use-page-tracking";
import { PageType } from "logger/page-tracking";
import { TrackingStepName } from "logger/user-tracking";
import AvailableTimes from "components/selection-components/select-time/available-times";
import { BookingClosed } from "components/selection-components/select-time/booking-closed";
import { TimeSetSkeleton } from "components/selection-components/select-time/time-set/time-set-skeleton";
import { AvailabilityState, isSectionShowable } from "utils/availability-utils";

function SelectTime() {
    const externalBookingSettings = useExternalBookingSettings();
    const state = useBookingStateContext();
    const { guests, date, isBookingRequest } = state;
    const availableTimes = useAvailableTimes(date);
    const filteredAvailableTimes = useFilteredAvailableTimes(date);
    const filteredExperiences = useFilteredExperiences(date);

    // Track when time step is active
    useStepTracking({
        stepName: TrackingStepName.TimeStep,
        autoTrack: true,
    });

    usePageTracking({
        pageName: "SelectTime",
        pageType: PageType.WizardStep,
        metadata: {
            guests: guests ?? 1,
            date: date?.toISOString(),
            isBookingRequest,
        },
    });

    if (availableTimes.isLoading || externalBookingSettings.isLoading) {
        return <TimeSetSkeleton />;
    }
    if (availableTimes.isError) {
        return <SomethingWrong />;
    }

    const allSectionsUnavailable = filteredAvailableTimes.every(
        (section) => !isSectionShowable(section.availabilityState)
    );
    // An information-only experience has nothing to book, so it joins the day's text rows instead
    // of hiding the closed message and those rows.
    const allExperiencesUnavailable =
        filteredExperiences.length === 0 ||
        filteredExperiences.every((exp) => exp.unavailable || exp.informationOnly);

    const noAvailableTimes =
        !isBookingRequest && allSectionsUnavailable && allExperiencesUnavailable;

    const hasAnyWaitList = filteredAvailableTimes.some(
        (section) => section.availabilityState === AvailabilityState.WaitListAvailable
    );

    const { messageBookingClosed, messageTemplates } = externalBookingSettings?.data ?? {};

    if (noAvailableTimes && !hasAnyWaitList) {
        return (
            <BookingClosed
                units={availableTimes.data ?? []}
                informationOnlyExperiences={filteredExperiences.filter(
                    (exp) => exp.informationOnly && !exp.unavailable
                )}
                messageBookingClosed={messageBookingClosed}
                messageBookingClosedTemplate={messageTemplates?.messageBookingClosed}
            />
        );
    }

    return <AvailableTimes />;
}

export { SelectTime };
